import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

export function AuthProvider({ children }) {
  // Restore user from cache immediately to prevent login flash
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem(USER_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(() => {
    // Only show loading if we have a token but need to verify
    return !!localStorage.getItem(TOKEN_KEY);
  });
  const tokenRef = useRef(localStorage.getItem(TOKEN_KEY));

  // Persist user to cache whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_KEY);
    }
  }, [user]);

  const saveToken = useCallback((newToken) => {
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);
      tokenRef.current = newToken;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      tokenRef.current = null;
    }
  }, []);

  // Verify token on mount
  useEffect(() => {
    let cancelled = false;

    const verifyToken = async (attempt = 1) => {
      const storedToken = localStorage.getItem(TOKEN_KEY);

      if (!storedToken) {
        if (!cancelled) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        const apiUrl = getApiUrl();
        const response = await axios.get(`${apiUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        });

        if (!cancelled) {
          setUser(response.data);
          setLoading(false);
        }
      } catch (error) {
        const status = error.response?.status;

        if (status === 401 || status === 403) {
          // Token is genuinely invalid — clear everything
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          tokenRef.current = null;
          if (!cancelled) {
            setUser(null);
            setLoading(false);
          }
        } else {
          // Network / transient error — retry once, then trust cache
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, 1500));
            if (!cancelled) return verifyToken(attempt + 1);
          }
          // After retry, trust cached user if available
          if (!cancelled) {
            // Keep cached user (set during useState init), just stop loading
            setLoading(false);
          }
        }
      }
    };

    verifyToken();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username, password) => {
    const apiUrl = getApiUrl();
    const response = await axios.post(`${apiUrl}/auth/login`, { username, password });

    saveToken(response.data.token);
    setUser(response.data.user);
    return response.data.user;
  }, [saveToken]);

  const logout = useCallback(() => {
    saveToken(null);
    setUser(null);
  }, [saveToken]);

  const value = {
    user,
    token: tokenRef.current ?? localStorage.getItem(TOKEN_KEY),
    loading,
    login,
    logout,
    isAdmin: user?.role === 'admin',
    isDriver: user?.role === 'driver',
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
